import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import axios from "axios";
import Header from "../components/Header";
import ChatBot from "../components/ChatBot";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ServicesPage = () => {
  const [content, setContent] = useState({
    services_title: "Hizmetlerimiz",
    services_highlight: "Profesyonel Protez Çözümleri",
    services_description: "Her türlü diş protezi ihtiyacınız için kapsamlı çözümler sunuyoruz.",
    services_hero_image: "",
    service1_title: "Zirkonyum Restorasyonlar",
    service1_desc: "Multilayer ve Bruxzir teknolojisi ile üstün estetik ve dayanıklılık.",
    service1_image: "",
    service2_title: "E.max & Cam Seramik",
    service2_desc: "Doğal diş görünümü için mükemmel estetik çözümler.",
    service2_image: "",
    service3_title: "İmplant Üstü Protezler",
    service3_desc: "Hibrit protezler ve custom abutment uygulamaları.",
    service3_image: "",
    service4_title: "Metal Destekli Protezler",
    service4_desc: "Geleneksel ve güvenilir protez çözümleri.",
    service4_image: ""
  });

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/content`);
      setContent(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error("Error fetching content:", error);
    }
  };

  const getImageUrl = (url) => {
    if (!url) return "";
    return url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
  };

  const services = [
    {
      title: content.service1_title,
      description: content.service1_desc,
      image: getImageUrl(content.service1_image),
      features: ["Multilayer Zirkonyum", "Bruxzir Solid", "Full Contour"]
    },
    {
      title: content.service2_title,
      description: content.service2_desc,
      image: getImageUrl(content.service2_image),
      features: ["E.max Press", "E.max CAD", "Cam Seramik Veneer"]
    },
    {
      title: content.service3_title,
      description: content.service3_desc,
      image: getImageUrl(content.service3_image),
      features: ["Hibrit Protez", "Custom Abutment", "Vida Tutuculu"]
    },
    {
      title: content.service4_title,
      description: content.service4_desc,
      image: getImageUrl(content.service4_image),
      features: ["Krom-Kobalt", "Döküm Metal", "Plaklar"]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="relative py-20 md:py-28 overflow-hidden" data-testid="services-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A192F] via-[#0d2341] to-[#0A192F]" />
        <div className="absolute top-10 right-10 w-72 h-72 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full blur-3xl opacity-20 animate-pulse" />
        <div className="absolute bottom-10 left-10 w-96 h-96 bg-gradient-to-r from-blue-400 to-cyan-300 rounded-full blur-3xl opacity-15" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center lg:text-left"
            >
              <p className="text-cyan-400 text-sm font-medium tracking-widest uppercase mb-4">
                {content.services_title}
              </p>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-cyan-300 bg-clip-text text-transparent">
                  {content.services_highlight}
                </span>
              </h1>
              <p className="text-white/70 text-lg max-w-2xl">
                {content.services_description}
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="hidden lg:block"
            >
              <img
                src={getImageUrl(content.services_hero_image)}
                alt="Hizmetlerimiz"
                className="rounded-2xl shadow-2xl w-full h-[350px] object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300"
                data-testid={`service-card-${index}`}
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F]/80 to-transparent" />
                  <h3 className="absolute bottom-4 left-6 text-white font-heading text-xl font-bold">
                    {service.title}
                  </h3>
                </div>
                <div className="p-6">
                  <p className="text-slate-600 mb-4">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-slate-700">
                        <Check className="w-4 h-4 text-cyan-500" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <ChatBot />
    </div>
  );
};

export default ServicesPage;
